import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useProfile } from '@/hooks/useProfile';

type InitialForumState = {
  categories?: ForumCategory[];
  threads?: ForumThread[];
  posts?: ForumPost[];
};

export interface ForumCategory {
  id: string;
  title: string;
  description?: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ForumThread {
  id: string;
  title: string;
  category_id: string;
  user_id: string | null;
  is_pinned: boolean;
  is_locked: boolean;
  views_count: number;
  created_at: string;
  updated_at: string;
  user?: ForumUser | null;
  posts?: ForumPost[];
  replies?: number;
  lastActivity?: string;
}

export interface ForumPost {
  id: string;
  thread_id: string;
  user_id: string | null;
  content: string;
  created_at: string;
  updated_at: string;
  user?: ForumUser | null;
  likes?: number;
  liked?: boolean;
}

export interface ForumUser {
  id: string;
  name: string;
  avatar: string | null;
  role: string;
}

export function useForum(initialState?: InitialForumState) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const hasBootstrappedRef = useRef(false);
  const [categories, setCategories] = useState<ForumCategory[]>(() => initialState?.categories || []);
  const [threads, setThreads] = useState<ForumThread[]>(() => initialState?.threads || []);
  const [posts, setPosts] = useState<ForumPost[]>(() => initialState?.posts || []);
  const [loading, setLoading] = useState(() => !(initialState?.categories || initialState?.threads));

  const hydrateUserMap = useCallback(async (userIds: string[]) => {
    if (userIds.length === 0) {
      return new Map<string, ForumUser>();
    }

    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, username, role')
      .in('id', userIds);

    if (error || !profiles) {
      return new Map<string, ForumUser>();
    }

    return new Map(
      profiles.map((profile: any) => [
        String(profile.id).trim(),
        {
          id: String(profile.id).trim(),
          name: profile.full_name?.trim() || profile.username?.trim() || 'User',
          avatar: profile.avatar_url || null,
          role: profile.role?.trim() || 'Member',
        },
      ])
    );
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('forum_categories').select('*').order('display_order', { ascending: true });
    if (error) {
      toast({ title: 'Error', description: 'Failed to load categories', variant: 'destructive' });
      setCategories([]);
    } else {
      setCategories((data || []) as ForumCategory[]);
    }
    setLoading(false);
  }, []);

  const fetchThreads = useCallback(async (categoryId?: string) => {
    setLoading(true);
    let query = supabase
      .from('forum_threads')
      .select('*')
      .order('is_pinned', { ascending: false })
      .order('updated_at', { ascending: false });

    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }

    const { data, error } = await query;
    if (error) {
      toast({ title: 'Error', description: 'Failed to load threads', variant: 'destructive' });
      setThreads([]);
      setLoading(false);
      return;
    }

    const threadRows = (data || []) as ForumThread[];
    const userIds = Array.from(new Set(threadRows.map((thread) => thread.user_id).filter(Boolean) as string[]));
    const userMap = await hydrateUserMap(userIds);
    const threadIds = threadRows.map((thread) => thread.id);

    let repliesMap: Record<string, number> = {};
    if (threadIds.length > 0) {
      const { data: postCounts } = await supabase.from('forum_posts').select('thread_id, id').in('thread_id', threadIds);
      repliesMap = (postCounts || []).reduce<Record<string, number>>((accumulator, post: any) => {
        accumulator[post.thread_id] = (accumulator[post.thread_id] || 0) + 1;
        return accumulator;
      }, {});
    }

    setThreads(
      threadRows.map((thread) => ({
        ...thread,
        user: thread.user_id ? userMap.get(String(thread.user_id).trim()) || null : null,
        replies: repliesMap[thread.id] || 0,
      }))
    );
    setLoading(false);
  }, [hydrateUserMap]);

  const fetchPosts = useCallback(async (threadId: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('forum_posts')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });

    if (error) {
      toast({ title: 'Error', description: 'Failed to load posts', variant: 'destructive' });
      setPosts([]);
      setLoading(false);
      return;
    }

    const postRows = (data || []) as ForumPost[];
    const userIds = Array.from(new Set(postRows.map((post) => post.user_id).filter(Boolean) as string[]));
    const userMap = await hydrateUserMap(userIds);

    setPosts(
      postRows.map((post) => ({
        ...post,
        user: post.user_id ? userMap.get(String(post.user_id).trim()) || null : null,
      }))
    );
    setLoading(false);
  }, [hydrateUserMap]);

  const addThread = useCallback(async (title: string, categoryId: string, content: string) => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('forum_threads')
      .insert({ title, category_id: categoryId, user_id: user.id })
      .select()
      .single();

    if (error) {
      toast({ title: 'Error', description: 'Failed to create thread', variant: 'destructive' });
      return null;
    }

    if (data) {
      await supabase.from('forum_posts').insert({ thread_id: data.id, user_id: user.id, content });
      await fetchThreads(categoryId);
      return data.id;
    }

    return null;
  }, [fetchThreads, user]);

  const addReply = useCallback(async (threadId: string, content: string) => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('forum_posts')
      .insert({ thread_id: threadId, user_id: user.id, content })
      .select()
      .single();

    if (error) {
      toast({ title: 'Error', description: 'Failed to post reply', variant: 'destructive' });
      return null;
    }

    await fetchPosts(threadId);
    await fetchThreads();

    const displayName = profile?.full_name || profile?.username || 'User';
    try {
      await supabase.functions.invoke('notify-thread-followers', {
        body: {
          thread_id: threadId,
          post_id: data.id,
          post_content: content,
          post_author_id: user.id,
          post_author_name: displayName,
          thread_title: threads.find((thread) => thread.id === threadId)?.title || 'Untitled Thread',
          post_url: typeof window !== 'undefined' ? window.location.href : '/forums',
        },
      });
    } catch (invokeError) {
      console.log('Notification function failed:', invokeError);
    }

    return data.id;
  }, [fetchPosts, fetchThreads, profile, threads, user]);

  const editThread = useCallback(async (threadId: string, title: string) => {
    const { error } = await supabase.from('forum_threads').update({ title }).eq('id', threadId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update thread', variant: 'destructive' });
      return false;
    }

    toast({ title: 'Success', description: 'Thread updated.' });
    await fetchThreads(threads.find((thread) => thread.id === threadId)?.category_id);
    return true;
  }, [fetchThreads, threads]);

  const editPost = useCallback(async (postId: string, content: string, threadId: string) => {
    const { error } = await supabase.from('forum_posts').update({ content, updated_at: new Date().toISOString() }).eq('id', postId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update post', variant: 'destructive' });
      return false;
    }

    toast({ title: 'Success', description: 'Post updated.' });
    await fetchPosts(threadId);
    return true;
  }, [fetchPosts]);

  const deleteThread = useCallback(async (threadId: string) => {
    const { error } = await supabase.from('forum_threads').delete().eq('id', threadId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete thread', variant: 'destructive' });
      return false;
    }

    toast({ title: 'Success', description: 'Thread deleted.' });
    await fetchThreads();
    return true;
  }, [fetchThreads]);

  const deletePost = useCallback(async (postId: string, threadId: string) => {
    const { count, error: countError } = await supabase
      .from('forum_posts')
      .select('*', { count: 'exact', head: true })
      .eq('thread_id', threadId);

    if (countError) {
      toast({ title: 'Error', description: 'Could not check thread contents.', variant: 'destructive' });
      return false;
    }

    if (count === 1) {
      return deleteThread(threadId);
    }

    const { error } = await supabase.from('forum_posts').delete().eq('id', postId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete post', variant: 'destructive' });
      return false;
    }

    toast({ title: 'Success', description: 'Post deleted.' });
    await fetchPosts(threadId);
    return true;
  }, [deleteThread, fetchPosts]);

  const toggleLike = useCallback(async (postId: string, liked: boolean) => {
    if (!user) return;

    if (liked) {
      const { error } = await supabase.from('forum_post_likes').delete().eq('post_id', postId).eq('user_id', user.id);
      if (error) {
        toast({ title: 'Error', description: 'Failed to unlike post', variant: 'destructive' });
      }
      return;
    }

    const { error } = await supabase.from('forum_post_likes').insert({ post_id: postId, user_id: user.id });
    if (error) {
      toast({ title: 'Error', description: 'Failed to like post', variant: 'destructive' });
    }
  }, [user]);

  const fetchLikes = useCallback(async (postId: string) => {
    const { data, error } = await supabase.from('forum_post_likes').select('*').eq('post_id', postId);
    if (error) return 0;
    return data.length;
  }, []);

  const fetchUserLiked = useCallback(async (postId: string) => {
    if (!user) return false;
    const { data, error } = await supabase
      .from('forum_post_likes')
      .select('*')
      .eq('post_id', postId)
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) return false;
    return !!data;
  }, [user]);

  const incrementThreadViews = useCallback(async (threadId: string) => {
    try {
      const { error } = await supabase.rpc('increment_thread_views', { thread_id: threadId });
      if (!error) {
        return;
      }

      const { data: thread, error: fetchError } = await supabase.from('forum_threads').select('views_count').eq('id', threadId).single();
      if (!fetchError && thread) {
        await supabase.from('forum_threads').update({ views_count: (thread.views_count || 0) + 1 }).eq('id', threadId);
      }
    } catch (error) {
      console.error('Increment thread views failed:', error);
    }
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel('realtime-forum-threads')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'forum_threads' }, (payload) => {
        const updatedThread = payload.new as ForumThread;
        setThreads((current) => current.map((thread) => (thread.id === updatedThread.id ? { ...thread, ...updatedThread } : thread)));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'forum_posts' }, (payload) => {
        const threadId = (payload.new as ForumPost).thread_id;
        setThreads((current) => current.map((thread) => (thread.id === threadId ? { ...thread, replies: (thread.replies || 0) + 1 } : thread)));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (hasBootstrappedRef.current) {
      return;
    }

    hasBootstrappedRef.current = true;

    if ((initialState?.categories?.length || 0) > 0 || (initialState?.threads?.length || 0) > 0) {
      setLoading(false);
      return;
    }

    void fetchCategories();
    void fetchThreads();
  }, [fetchCategories, fetchThreads, initialState?.categories, initialState?.threads]);

  return {
    categories,
    threads,
    posts,
    loading,
    setPosts,
    fetchCategories,
    fetchThreads,
    fetchPosts,
    addThread,
    addReply,
    toggleLike,
    fetchLikes,
    fetchUserLiked,
    incrementThreadViews,
    editThread,
    editPost,
    deleteThread,
    deletePost,
  };
}
